import State from "../../models/State"
import type {RequestDuckState} from "../../ducks/request/types"
import {TextModel} from "../../models/Text"
import {capitalizeFirstLetterOfEachWord} from "../capitalizeFirstLetter"
import type {Filter} from "../getFiltersFromMerchandisedPath"
import extractPriceAndSortFilters from "../extractPriceAndSortFilters"

export type PathType = "shop" | "brands" | "promotions"

export type SegmentsAndFilters = {
    pathType: PathType
    segments: string[]
    filters: Filter[]
    multipleFacetFromSameFilter: boolean
}

export const getSeoTitleForMerchandisePath = (state: State): string => {
    const path = preparePath(state.request)

    const sortRemovedUrl = removePriceAndSortFromUrl(path)
    const segmentsAndFilters = getSegmentsAndFiltersFromMerchandisePath(sortRemovedUrl, state)

    const updatedSegmentsAndFilters = getGenderUpdated(segmentsAndFilters, state.text)

    const seoTitle =
        updatedSegmentsAndFilters.pathType === "shop"
            ? getTitleForShopUrl(updatedSegmentsAndFilters)
            : getTitleForBrandsAndPromotionsUrl(updatedSegmentsAndFilters, updatedSegmentsAndFilters.pathType)
    const capitalizedSeoTitle = capitalizeFirstLetterOfEachWord(seoTitle, ["and"])
    return capitalizedSeoTitle
}

export const preparePath = (request: RequestDuckState): string => request.url.replace(request.siteUrl, "").split("?")[0]

export const getTitleForShopUrl = (segmentsAndFilter: SegmentsAndFilters): string => {
    const {segments, filters} = segmentsAndFilter
    const [firstSegment, secondSegment, thirdSegment, fourthSegment] = segments
    const [firstFilter, secondFilter, thirdFilter] = filters
    const segmentsLength = segments.length
    const filtersLength = filters.length

    if (segmentsAndFilter.multipleFacetFromSameFilter) return firstSegment || "Shop"

    if (segmentsLength === 0) {
        if (filtersLength === 1) {
            return `${firstFilter.filterValue}`
        }
        if (filtersLength === 2) {
            return `${firstFilter.filterValue} ${secondFilter.filterValue}`
        }
        if (filtersLength === 3) {
            return `${firstFilter.filterValue} ${secondFilter.filterValue} ${thirdFilter.filterValue}`
        }
        return "Shop"
    }

    if (segmentsLength === 1) {
        if (filtersLength === 0) {
            return `${firstSegment}`
        }
        if (filtersLength === 1) {
            return `${firstSegment} ${firstFilter.filterValue}`
        }
        if (filtersLength === 2) {
            return `${firstSegment} ${firstFilter.filterValue} ${secondFilter.filterValue}`
        }
        if (filtersLength === 3) {
            return `${firstSegment} ${firstFilter.filterValue} ${secondFilter.filterValue} ${thirdFilter.filterValue}`
        }
    }

    if (segmentsLength === 2) {
        if (filtersLength === 0) {
            return `${firstSegment} ${secondSegment}`
        }
        if (filtersLength === 1) {
            return `${firstSegment} ${secondSegment} ${firstFilter.filterValue}`
        }
        if (filtersLength === 2) {
            return `${firstSegment} ${secondSegment} ${firstFilter.filterValue} ${secondFilter.filterValue}`
        }
        if (filtersLength === 3) {
            return `${firstSegment} ${secondSegment} ${firstFilter.filterValue} ${secondFilter.filterValue} ${thirdFilter.filterValue}`
        }
    }

    if (segmentsLength === 3) {
        if (filtersLength === 0) {
            return `${firstSegment} ${thirdSegment}`
        }
        if (filtersLength === 1) {
            return `${firstSegment} ${thirdSegment} ${firstFilter.filterValue}`
        }
        if (filtersLength === 2) {
            return `${firstSegment} ${thirdSegment} ${firstFilter.filterValue} ${secondFilter.filterValue}`
        }
        if (filtersLength === 3) {
            return `${firstSegment} ${thirdSegment} ${firstFilter.filterValue} ${secondFilter.filterValue} ${thirdFilter.filterValue}`
        }
    }

    if (segmentsLength === 4) {
        if (filtersLength === 0) {
            return `${firstSegment} ${fourthSegment}`
        }
        if (filtersLength === 1) {
            return `${firstSegment} ${fourthSegment} ${firstFilter.filterValue}`
        }
        if (filtersLength === 2) {
            return `${firstSegment} ${fourthSegment} ${firstFilter.filterValue} ${secondFilter.filterValue}`
        }
        if (filtersLength === 3) {
            return `${firstSegment} ${fourthSegment} ${firstFilter.filterValue} ${secondFilter.filterValue} ${thirdFilter.filterValue}`
        }
    }
    return firstSegment
}

export const getTitleForBrandsAndPromotionsUrl = (
    segmentsAndFilter: SegmentsAndFilters,
    pathType: PathType,
): string => {
    const {segments, filters} = segmentsAndFilter
    const [firstSegment, secondSegment, thirdSegment, fourthSegment, fifthSegment] = segments
    const [firstFilter, secondFilter, thirdFilter] = filters
    const segmentsLength = segments.length
    const filtersLength = filters.length

    if (segmentsAndFilter.multipleFacetFromSameFilter) return firstSegment || capitalizeFirstLetterOfEachWord(pathType)
    if (segmentsLength === 0) {
        if (filtersLength === 1) {
            return firstFilter.filterValue
        }
        if (filtersLength === 2) {
            return `${firstFilter.filterValue} ${secondFilter.filterValue}`
        }
        if (filtersLength === 3) {
            return `${firstFilter.filterValue} ${secondFilter.filterValue} ${thirdFilter.filterValue}`
        }
        return capitalizeFirstLetterOfEachWord(pathType)
    }
    if (segmentsLength === 1 && filtersLength === 0) {
        return firstSegment
    }
    if (segmentsLength === 2 && filtersLength === 0) {
        return `${secondSegment} ${firstSegment}`
    }
    if (segmentsLength === 3 && filtersLength === 0) {
        return `${secondSegment} ${firstSegment} ${thirdSegment}`
    }
    if (segmentsLength === 4) {
        if (filtersLength === 0) {
            return `${secondSegment} ${firstSegment} ${fourthSegment}`
        }
        if (filtersLength === 1) {
            return `${secondSegment} ${firstSegment} ${fourthSegment} ${firstFilter.filterValue}`
        }
        if (filtersLength === 2) {
            return `${secondSegment} ${firstSegment} ${fourthSegment} ${firstFilter.filterValue} ${secondFilter.filterValue}`
        }
        if (filtersLength === 3) {
            return `${secondSegment} ${firstSegment} ${fourthSegment} ${firstFilter.filterValue} ${secondFilter.filterValue} ${thirdFilter.filterValue}`
        }
    }

    if (segmentsLength === 5) {
        if (filtersLength === 0) {
            return `${secondSegment} ${firstSegment} ${fifthSegment}`
        }
        if (filtersLength === 1) {
            return `${secondSegment} ${firstSegment} ${fifthSegment} ${firstFilter.filterValue}`
        }
        if (filtersLength === 2) {
            return `${secondSegment} ${firstSegment} ${fifthSegment} ${firstFilter.filterValue} ${secondFilter.filterValue}`
        }
        if (filtersLength === 3) {
            return `${secondSegment} ${firstSegment} ${fifthSegment} ${firstFilter.filterValue} ${secondFilter.filterValue} ${thirdFilter.filterValue}`
        }
    }

    return firstSegment
}

export const removePriceAndSortFromUrl = (url: string): string => url.replace(extractPriceAndSortFilters(url), "")

export const getPathType = (path: string): PathType => {
    if (path.startsWith("/promotions")) return "promotions"
    if (path.startsWith("/brands")) return "brands"
    return "shop"
}

export const getSegmentsAndFiltersFromMerchandisePath = (path: string, state: State): SegmentsAndFilters => {
    const pathParts = path.split("/").slice(1)
    const segments: string[] = []
    let filters: Filter[] = []
    let multipleFacetFromSameFilter = false
    for (let i = 1; i < pathParts.length; i += 1) {
        const part = pathParts[i]
        if (part === "f") {
            filters = getSelectedFacetsFromState(state)
            multipleFacetFromSameFilter = isMultipleFacetFromSameFilter(filters)
            break
        } else {
            segments.push(part.replaceAll("-", " "))
        }
    }

    const segmentsAndFilters: SegmentsAndFilters = {
        pathType: getPathType(path),
        segments,
        filters,
        multipleFacetFromSameFilter,
    }

    return segmentsAndFilters
}

export const isMultipleFacetFromSameFilter = (filters: Filter[]) =>
    filters.reduce((setOfFilters, currentFilter) => {
        setOfFilters.add(currentFilter.filterName)
        return setOfFilters
    }, new Set()).size !== filters.length

export const getSelectedFacetsFromState = (state: State): Filter[] => {
    const {facets} = state.search
    const selectedFacets: Filter[] = Object.keys(facets)
        .map(key => {
            if (facets[key].s) {
                return {filterName: facets[key].v.split(":")[0], filterValue: facets[key].n}
            }
            return {filterName: "", filterValue: ""}
        })
        .filter(a => a.filterName)
    return selectedFacets
}

export const getGenderUpdated = (segmentsAndFilters: SegmentsAndFilters, text: TextModel): SegmentsAndFilters => {
    const {
        pages: {
            seoMetaData: {genderValues},
        },
    } = text
    const updatedSegmentsAndFilters = {...segmentsAndFilters}
    const {pathType, segments} = updatedSegmentsAndFilters
    if (pathType === "shop" && segments.length && genderValues[segments[0]]) {
        segments[0] = genderValues[segments[0]]
    }
    if ((pathType === "brands" || pathType === "promotions") && segments.length > 1 && genderValues[segments[1]]) {
        segments[1] = genderValues[segments[1]]
    }
    return updatedSegmentsAndFilters
}
